import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { BrandWordmark } from "@/components/Brand";

const Header = ({ onRequestDemo }) => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "solucoes", label: t.nav.solutions },
    { id: "canais", label: t.nav.channels },
    { id: "diferenciais", label: t.nav.differentiators },
    { id: "seguranca", label: t.nav.security },
    { id: "contato", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050816]/75 border-b border-[#1dc7e9]/12"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[64px] md:h-[72px] lg:h-[76px] flex items-center justify-between gap-6">
        <a
          href="#top"
          className="flex items-center -my-2 shrink-0"
          data-testid="header-logo"
        >
          <BrandWordmark
            height={50}
            className="h-[34px] sm:h-[38px] md:h-[44px] lg:h-[50px] w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className="text-[13.5px] text-[#b8c0cc] hover:text-white transition-colors duration-200 font-medium"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1dc7e9]/20 text-[#b8c0cc] hover:text-white hover:border-[#1dc7e9]/50 transition-all duration-200 text-xs font-semibold mono"
            data-testid="language-toggle"
            aria-label="Toggle language"
          >
            <Globe size={13} className="text-[#1dc7e9]" />
            {lang.toUpperCase()}
          </button>

          <button
            onClick={onRequestDemo}
            className="hidden sm:inline-flex btn-primary items-center gap-2 bg-[#1dc7e9] text-[#04060d] font-bold text-[13px] tracking-tight px-4 py-2.5 rounded-lg hover:shadow-[0_0_30px_rgba(29,199,233,0.45)] hover:-translate-y-0.5 transition-all duration-300"
            data-testid="header-demo-btn"
          >
            {t.nav.requestDemo}
          </button>

          <button
            className="lg:hidden text-white p-2 rounded-md hover:bg-white/5"
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-[#1dc7e9]/10 bg-[#050816]/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  className="text-base text-[#b8c0cc] hover:text-white py-1"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </a>
              ))}
              <div className="flex items-center gap-3 pt-3">
                <button
                  onClick={toggle}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1dc7e9]/20 text-[#b8c0cc] text-xs font-semibold mono"
                  data-testid="mobile-language-toggle"
                >
                  <Globe size={13} className="text-[#1dc7e9]" />
                  {lang.toUpperCase()}
                </button>
                <button
                  onClick={() => {
                    setOpen(false);
                    onRequestDemo?.();
                  }}
                  className="flex-1 bg-[#1dc7e9] text-[#04060d] font-bold text-sm px-4 py-2.5 rounded-lg"
                  data-testid="mobile-demo-btn"
                >
                  {t.nav.requestDemo}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
